import React from 'react';

import type { ContentLocation } from '@gitbook/api-client';
import { Alert, Badge, LoadingIndicator } from '@gitbook/spine-react';

import { BacklinksList } from './BacklinksList';

/**
 * Wrap the backlinks verification process.
 * To avoid layout shift in a modal, we use a min-height to reserve space for the loader.
 */
export function BacklinksAlertWrapper(props: {
    // The type of content being checked for backlinks
    contentType: 'page' | 'file';
    children: React.ReactNode;
}) {
    const { contentType, children } = props;
    return (
        <div className="min-h-6">
            <React.Suspense fallback={<BacklinksAlertLoading contentType={contentType} />}>
                {children}
            </React.Suspense>
        </div>
    );
}

/**
 * Render a warning alert for content that has backlinks.
 */
export function BacklinksAlert(props: {
    // The type of content being checked for backlinks
    contentType: 'page' | 'file';
    // The content locations that link to this content
    contentLocations: ContentLocation[];
    onNavigate: () => void;
}): React.ReactElement {
    const { contentType, contentLocations, onNavigate } = props;

    return (
        <Alert
            kind="info"
            title="Check completed"
            details={
                <div className="flex flex-col gap-3">
                    <div>{getHasBacklinksDescription(contentType)}</div>
                    <BacklinksList contentLocations={contentLocations} onNavigate={onNavigate} />
                </div>
            }
        />
    );
}

/**
 * Loader fallback for the backlinks warning.
 */
function BacklinksAlertLoading(props: { contentType: 'page' | 'file' }): React.ReactElement {
    const { contentType } = props;

    return (
        <div>
            <Badge
                kind="secondary"
                icon={LoadingIndicator}
                label={
                    contentType
                        ? 'Checking for links to this page...'
                        : 'Checking for references to this file...'
                }
            />
        </div>
    );
}

function getHasBacklinksDescription(contentType: 'page' | 'file'): string {
    switch (contentType) {
        case 'page':
            return 'Deleting this page will break links in the following locations:';
        case 'file':
            return 'Deleting this file will break images or links in the following locations:';
    }
}
